import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Container, Row, Col, Form, InputGroup, Spinner, Badge, Button, Offcanvas, Alert, Card } from 'react-bootstrap';
import { Search, Filter, MapPin, Star, ShoppingBag, UtensilsCrossed, Plus } from 'lucide-react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { catalogAPI, MasterItem, Vertical } from '../api/catalog';
import { cartAPI } from '../api/cart';
import { useAuthStore } from '../stores/authStore';
import SmartImage from '../components/SmartImage';
import ProductCardSkeleton from '../components/ProductCardSkeleton';
import HeroBanner from '../components/HeroBanner';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';

const PAGE_SIZE = 60;

const Catalog: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const verticalParam = (searchParams.get('vertical') as Vertical) || 'grocery';
  const vertical: Vertical = verticalParam === 'food' ? 'food' : 'grocery';
  const categoryParam = searchParams.get('category');
  const [items, setItems] = useState<MasterItem[]>([]);
  const [categories, setCategories] = useState<{ category: string; count: number }[]>([]);
  const [activeCategory, setActiveCategory] = useState<string | null>(categoryParam);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Item-detail drawer state
  const [drawerItem, setDrawerItem] = useState<MasterItem | null>(null);
  const [storeRows, setStoreRows] = useState<any[]>([]);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [inStockOnly, setInStockOnly] = useState(true);
  const [addingId, setAddingId] = useState<number | null>(null);

  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuthStore();

  const handleAddToCart = async (row: any) => {
    // Cart is a customer-only feature — backend's /api/cart/add is gated by
    // get_current_customer. Without this guard, store-owner / delivery-agent /
    // admin sessions would hit a 403 with a confusing toast. Send them
    // somewhere useful instead.
    if (!isAuthenticated) {
      toast('Please log in as a customer to add items to your cart.', { icon: '🔐' });
      navigate('/login');
      return;
    }
    if (user && user.role !== 'customer') {
      toast.error(
        `You're logged in as ${user.role.replace('_', ' ')}. Switch to a customer account to shop.`,
        { duration: 4000 },
      );
      return;
    }
    if (row.stock_quantity <= 0) return;
    try {
      setAddingId(row.product_id);
      await cartAPI.addToCart(row.store_id, row.product_id, 1);
      toast.success(`Added to cart from ${row.store_name}`, { duration: 1800 });
    } catch (e: any) {
      // Surface the actual server reason (stock conflict, store closed, etc.)
      // instead of a flat "Could not add to cart" — the user can act on it.
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      if (status === 401) {
        toast.error('Your session has expired — please log in again.');
      } else if (status === 403) {
        toast.error('Add to cart is for customer accounts only.');
      } else {
        toast.error(detail || `Could not add to cart (HTTP ${status || 'network'}).`);
      }
    } finally {
      setAddingId(null);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    // Load items + categories independently — if categories fail we still
    // want to show the products grid (and vice versa). Previously a single
    // 5xx anywhere produced the dreaded "Failed to load catalog" with no
    // way to recover.
    const itemsP = catalogAPI.listMasterItems({
      q: q.trim() || undefined,
      category: activeCategory || undefined,
      vertical,
      limit: PAGE_SIZE,
    }).then((its) => setItems(its))
      .catch((e) => { setError(e.response?.data?.detail || 'Failed to load catalog items'); setItems([]); });

    const catsP = catalogAPI.listCategories({ vertical })
      .then((cs) => setCategories(cs as any))
      .catch(() => setCategories([]));

    await Promise.all([itemsP, catsP]);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, activeCategory, vertical]);

  useEffect(() => { load(); }, [load]);

  // Keep `activeCategory` in sync with the `?category=` URL param so that
  // navigating here from another page (e.g. the home grid) actually applies
  // the filter, and so the browser back button works as expected.
  useEffect(() => {
    setActiveCategory(categoryParam);
  }, [categoryParam]);

  const openDrawer = async (item: MasterItem) => {
    setDrawerItem(item);
    setDrawerLoading(true);
    setStoreRows([]);
    try {
      const data = await catalogAPI.getStoresCarryingItem(item.id, inStockOnly);
      setStoreRows(data.stores);
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Failed to load store list');
    } finally {
      setDrawerLoading(false);
    }
  };

  const refreshDrawer = async (nextInStockOnly: boolean) => {
    if (!drawerItem) return;
    setInStockOnly(nextInStockOnly);
    setDrawerLoading(true);
    try {
      const data = await catalogAPI.getStoresCarryingItem(drawerItem.id, nextInStockOnly);
      setStoreRows(data.stores);
    } finally {
      setDrawerLoading(false);
    }
  };

  const grouped = useMemo(() => {
    const m = new Map<string, MasterItem[]>();
    items.forEach((it) => {
      const arr = m.get(it.category) || [];
      arr.push(it);
      m.set(it.category, arr);
    });
    return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [items]);

  return (
    <Container className="py-4">
      {/* Promo carousel — only on the grocery vertical, hidden on food */}
      {vertical === 'grocery' && (
        <div className="mb-4">
          <HeroBanner />
        </div>
      )}

      {/* Vertical switcher — Grocery vs Food */}
      <div className="d-flex gap-2 mb-3">
        <Button
          variant={vertical === 'grocery' ? 'success' : 'outline-success'}
          onClick={() => {
            const next = new URLSearchParams(searchParams);
            next.set('vertical', 'grocery');
            setSearchParams(next, { replace: true });
            setActiveCategory(null);
          }}
        >
          <ShoppingBag size={16} className="me-2" />Grocery
        </Button>
        <Button
          variant={vertical === 'food' ? 'danger' : 'outline-danger'}
          onClick={() => {
            const next = new URLSearchParams(searchParams);
            next.set('vertical', 'food');
            setSearchParams(next, { replace: true });
            setActiveCategory(null);
          }}
        >
          <UtensilsCrossed size={16} className="me-2" />Food
        </Button>
      </div>

      <div className="d-flex flex-wrap align-items-end justify-content-between gap-3 mb-3">
        <div>
          <h2 className="fw-bold mb-1">{vertical === 'food' ? 'Restaurant Menu' : 'Browse Catalog'}</h2>
          <p className="text-muted mb-0">
            {vertical === 'food'
              ? 'Find any dish — biryani, curries, breads, drinks — then pick the restaurant.'
              : 'Find any product, then pick the store with the best price.'}
          </p>
        </div>
        <Form
          onSubmit={(e) => { e.preventDefault(); load(); }}
          style={{ minWidth: 280, flex: '1 1 320px', maxWidth: 480 }}
        >
          <InputGroup>
            <InputGroup.Text className="bg-white"><Search size={16} /></InputGroup.Text>
            <Form.Control
              placeholder="Search products…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <Button type="submit" variant="success">Search</Button>
          </InputGroup>
        </Form>
      </div>

      {/* Category chips */}
      <div className="d-flex flex-wrap gap-2 mb-4">
        <Badge
          bg={activeCategory === null ? 'success' : 'light'}
          text={activeCategory === null ? 'light' : 'dark'}
          style={{ cursor: 'pointer', padding: '8px 14px', borderRadius: 999, border: '1px solid var(--pm-color-border, #E5E7EB)' }}
          onClick={() => {
            setActiveCategory(null);
            const next = new URLSearchParams(searchParams);
            next.delete('category');
            setSearchParams(next, { replace: true });
          }}
        >
          All
        </Badge>
        {categories.map((c) => (
          <Badge
            key={c.category}
            bg={activeCategory === c.category ? 'success' : 'light'}
            text={activeCategory === c.category ? 'light' : 'dark'}
            style={{ cursor: 'pointer', padding: '8px 14px', borderRadius: 999, border: '1px solid var(--pm-color-border, #E5E7EB)' }}
            onClick={() => {
              setActiveCategory(c.category);
              const next = new URLSearchParams(searchParams);
              next.set('category', c.category);
              setSearchParams(next, { replace: true });
            }}
            title={`${c.count} item${c.count === 1 ? '' : 's'}`}
          >
            {c.category} <span className="text-muted ms-1" style={{ fontSize: 11 }}>{c.count}</span>
          </Badge>
        ))}
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <ProductCardSkeleton count={12} />
      ) : items.length === 0 ? (
        <Alert variant="info">No items match your search.</Alert>
      ) : (
        grouped.map(([cat, list]) => (
          <section key={cat} className="mb-4">
            <h5 className="fw-bold mb-3">{cat} <span className="text-muted small">· {list.length}</span></h5>
            <Row className="g-3">
              {list.map((it, idx) => (
                <Col key={it.id} xs={6} sm={4} md={3} lg={2}>
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25, delay: Math.min(idx * 0.02, 0.3) }}
                    whileHover={{ y: -4 }}
                  >
                  <Card
                    className="h-100 border-0 shadow-sm"
                    style={{ cursor: 'pointer', borderRadius: 12 }}
                    onClick={() => openDrawer(it)}
                  >
                    <div style={{ height: 110, background: 'var(--pm-color-surface-2, #F3F4F6)', borderTopLeftRadius: 12, borderTopRightRadius: 12, overflow: 'hidden' }}>
                      <SmartImage
                        src={it.image_url || undefined}
                        thumb={it.thumbnail_url || undefined}
                        alt={it.name}
                        size="thumb"
                        radius={0}
                      />
                    </div>
                    <Card.Body className="p-2">
                      <div className="fw-semibold" style={{ fontSize: 13, lineHeight: 1.25 }}>{it.name}</div>
                      <div className="text-muted" style={{ fontSize: 11 }}>{it.unit}{it.default_weight ? ` · ${it.default_weight}` : ''}</div>
                    </Card.Body>
                  </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </section>
        ))
      )}

      <Offcanvas show={!!drawerItem} onHide={() => setDrawerItem(null)} placement="end" style={{ width: 'min(92vw, 480px)' }}>
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>{drawerItem?.name}</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {drawerItem && (
            <>
              <div className="d-flex align-items-center gap-3 mb-3">
                {drawerItem.image_url && (
                  <img src={drawerItem.image_url} alt={drawerItem.name}
                    style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, background: '#f3f4f6' }} />
                )}
                <div>
                  <div className="text-muted small">{drawerItem.category}</div>
                  <div className="small">{drawerItem.unit}{drawerItem.default_weight ? ` · ${drawerItem.default_weight}` : ''}</div>
                  <Badge bg={drawerItem.is_veg ? 'success' : 'danger'} className="mt-1">{drawerItem.is_veg ? 'VEG' : 'NON-VEG'}</Badge>
                </div>
              </div>

              <Form.Check
                type="switch"
                id="in-stock-only"
                label="In stock only"
                checked={inStockOnly}
                onChange={(e) => refreshDrawer(e.target.checked)}
                className="mb-3"
              />

              <h6 className="fw-bold mb-2"><Filter size={14} className="me-1" />Available at {storeRows.length} store{storeRows.length === 1 ? '' : 's'}</h6>

              {drawerLoading ? (
                <div className="text-center py-4"><Spinner animation="border" size="sm" className="text-success" /></div>
              ) : storeRows.length === 0 ? (
                <Alert variant="info" className="small">No stores currently carry this item{inStockOnly ? ' in stock' : ''}.</Alert>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {storeRows.map((row) => (
                    <Card key={row.product_id} className="border-0 shadow-sm">
                      <Card.Body className="p-3">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <Link to={`/store/${row.store_id}`} className="fw-semibold text-decoration-none text-dark">
                              {row.store_name}
                            </Link>
                            <div className="text-muted small d-flex align-items-center gap-2 mt-1">
                              {row.store_city && (<><MapPin size={11} /> {row.store_city}</>)}
                              {row.store_rating > 0 && (<><Star size={11} fill="#F59E0B" stroke="#F59E0B" /> {row.store_rating.toFixed(1)}</>)}
                              {row.store_is_open ? <Badge bg="success" pill style={{ fontSize: 9 }}>Open</Badge> : <Badge bg="secondary" pill style={{ fontSize: 9 }}>Closed</Badge>}
                            </div>
                          </div>
                          <div className="text-end">
                            <div className="fw-bold" style={{ color: 'var(--pm-color-primary, #16A34A)' }}>₹{row.effective_price}</div>
                            {row.mrp && row.mrp > row.effective_price && (
                              <div className="text-muted text-decoration-line-through small">₹{row.mrp}</div>
                            )}
                            {row.effective_discount_percent > 0 && (
                              <Badge bg="warning" text="dark" style={{ fontSize: 10 }}>{row.effective_discount_percent.toFixed(0)}% off</Badge>
                            )}
                          </div>
                        </div>
                        <div className="d-flex justify-content-between align-items-center mt-2 gap-2">
                          <small className="text-muted">{row.stock_quantity > 0 ? `${row.stock_quantity} in stock` : 'Out of stock'}</small>
                          <div className="d-flex gap-2">
                            <Button
                              size="sm"
                              variant="success"
                              className="fw-semibold d-inline-flex align-items-center gap-1"
                              disabled={row.stock_quantity <= 0 || !row.store_is_open || addingId === row.product_id}
                              onClick={() => handleAddToCart(row)}
                            >
                              {addingId === row.product_id ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                <><Plus size={14} /> Add</>
                              )}
                            </Button>
                            <Link to={`/store/${row.store_id}`}>
                              <Button size="sm" variant="outline-success">Visit store →</Button>
                            </Link>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              )}
            </>
          )}
        </Offcanvas.Body>
      </Offcanvas>
    </Container>
  );
};

export default Catalog;
